/**
 * Operator access calls. Shapes follow the frozen contract of
 * /operator/access/*. Every mutation carries a reason code, a free-text reason
 * and `confirm: true` (interim step-up policy).
 */
import { operatorGet, operatorSend, type OperatorFetch } from "../operatorHttp";

export const REASON_CODES = [
  "onboarding",
  "offboarding",
  "role_change",
  "access_review",
  "incident",
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

export const MAX_REASON_CHARS = 256;
export const MAX_DISPLAY_NAME_CHARS = 80;
export const MAX_SUBJECT_CHARS = 256;
export const MAX_ROLES_PER_CREATE = 10;

export const MANAGE_PERMISSION = "operators.manage";
export const READ_PERMISSION = "operators.read";

export interface OperatorMe {
  operator_id: string;
  display_name: string | null;
  roles: string[];
  permissions: string[];
  session_issued_at: string;
  step_up_valid_until: string;
}

export interface OperatorRoleRef {
  role_id: string;
  display_name: string;
  active: boolean;
}

export interface OperatorSummary {
  operator_id: string;
  display_name: string | null;
  subject_hint: string;
  active: boolean;
  created_at: string | null;
  deactivated_at: string | null;
  roles: OperatorRoleRef[];
  linked_user_id: string | null;
  last_sign_in_at: string | null;
  is_self: boolean;
}

export interface OperatorDetail extends OperatorSummary {
  issuer: string;
  /** Present only for callers holding operators.manage. */
  subject?: string;
  effective_permissions: string[];
  active_sessions: number;
  created_by: string | null;
}

export interface RoleItem {
  role_id: string;
  display_name: string;
  active: boolean;
  permissions: string[];
  operator_count: number;
  grantable: boolean;
}

export interface AccessChange {
  reason_code: string;
  reason: string;
  confirm?: boolean;
}

export type MutationStatus =
  | "created"
  | "updated"
  | "assigned"
  | "unassigned"
  | "deactivated"
  | "activated"
  | "unchanged";

export interface MutationResult {
  status: MutationStatus;
  operator: OperatorDetail;
}

export interface CreateOperatorBody extends AccessChange {
  subject: string;
  display_name: string;
  linked_user_id: string | null;
  role_ids: string[];
}

export interface UpdateOperatorBody extends AccessChange {
  display_name?: string;
  linked_user_id?: string | null;
}

export interface AccessApi {
  me(): Promise<OperatorMe>;
  listOperators(): Promise<OperatorSummary[]>;
  operator(id: string): Promise<OperatorDetail>;
  roles(): Promise<RoleItem[]>;
  createOperator(body: CreateOperatorBody): Promise<MutationResult>;
  updateOperator(id: string, body: UpdateOperatorBody): Promise<MutationResult>;
  deactivate(id: string, change: AccessChange): Promise<MutationResult>;
  activate(id: string, change: AccessChange): Promise<MutationResult>;
  assignRole(
    id: string,
    roleId: string,
    change: AccessChange,
  ): Promise<MutationResult>;
  unassignRole(
    id: string,
    roleId: string,
    change: AccessChange,
  ): Promise<MutationResult>;
  /** Ends the operator session; the caller then starts the sign-in. */
  reauthenticate(): Promise<void>;
}

export function createAccessApi(
  fetcher: OperatorFetch = (...args) => fetch(...args),
): AccessApi {
  const base = (id: string) => `/access/operators/${encodeURIComponent(id)}`;
  return {
    me: () => operatorGet(fetcher, "/access/me"),
    listOperators: async () =>
      (
        await operatorGet<{ items: OperatorSummary[] }>(
          fetcher,
          "/access/operators",
        )
      ).items,
    operator: (id) => operatorGet(fetcher, base(id)),
    roles: async () =>
      (await operatorGet<{ items: RoleItem[] }>(fetcher, "/access/roles"))
        .items,
    createOperator: (body) =>
      operatorSend(fetcher, "POST", "/access/operators", body),
    updateOperator: (id, body) =>
      operatorSend(fetcher, "PATCH", base(id), body),
    deactivate: (id, change) =>
      operatorSend(fetcher, "POST", `${base(id)}/deactivate`, change),
    activate: (id, change) =>
      operatorSend(fetcher, "POST", `${base(id)}/activate`, change),
    assignRole: (id, roleId, change) =>
      operatorSend(fetcher, "POST", `${base(id)}/roles/assign`, {
        ...change,
        role_id: roleId,
      }),
    unassignRole: (id, roleId, change) =>
      operatorSend(fetcher, "POST", `${base(id)}/roles/unassign`, {
        ...change,
        role_id: roleId,
      }),
    reauthenticate: async () => {
      try {
        await operatorSend(fetcher, "POST", "/auth/logout", {});
      } catch {
        // A failed logout still leads to the sign-in screen.
      }
    },
  };
}
